/**
 * Garden geometry shared by the server, the Claude connector and the app:
 * biome maps in the Thicket RPG Lab's format (16 rows of 24 letters, 16px
 * tiles) and where things may be planted. The game rules don't use it; the
 * API uses it to refuse planting in water or on paths, and to find a free
 * spot when Claude plants something.
 *
 * Letters follow the Lab: g grass, p path, c cliff, w pond, F forest,
 * u upper ledge, l low ground, r river, x stairs, W waterfall, e earth wall,
 * q flagstones, plus b for the raised bed's tiles.
 */
import { LAB_MAPS } from './lab-maps.js';
import { SUGGESTED_SPOTS } from './suggested-spots.js';
import { MEGA_WIDTH } from './tree-sizes.js';
import type { Biome, Difficulty, GameState, Plant } from './types.js';

export const TILE = 16;
export const COLS = 24;
export const ROWS = 16;

/** Open flat ground: grass, low ground and ledge or mesa tops. */
export const PLANTABLE = new Set(['g', 'l', 'u']);

export interface BiomeMap {
  rows: string[];
  /** Tiles that hold the raised bed, row-major. */
  bed: [number, number][];
  /** Tiles taken by fixed props (rocks, wild trees, ruins), so nothing is planted under them. */
  blocked: Set<string>;
}

export const tileKey = (c: number, r: number) => `${c},${r}`;

export function parseMap(rows: string[], blocked: Iterable<[number, number]> = []): BiomeMap {
  if (rows.length !== ROWS || rows.some((r) => r.length !== COLS)) {
    throw new Error(`A map needs ${ROWS} rows of ${COLS} letters`);
  }
  const bed: [number, number][] = [];
  rows.forEach((row, r) => [...row].forEach((ch, c) => ch === 'b' && bed.push([c, r])));
  return { rows, bed, blocked: new Set([...blocked].map(([c, r]) => tileKey(c, r))) };
}

export const letterAt = (m: BiomeMap, c: number, r: number) => m.rows[r]?.[c] ?? '';
export const isPlantable = (m: BiomeMap, c: number, r: number) => PLANTABLE.has(letterAt(m, c, r)) && !m.blocked.has(tileKey(c, r));
export const isWalkable = (m: BiomeMap, c: number, r: number) => PLANTABLE.has(letterAt(m, c, r)) || ['p', 'q', 'x'].includes(letterAt(m, c, r));

/** A plant's saved position is its tile's anchor, the Lab's X(tx) = tx×16+8 and Y(ty) = ty×16+14. */
export const anchorOf = (c: number, r: number) => ({ x: c * TILE + 8, y: r * TILE + 14 });
export const tileAt = (x: number, y: number) => ({ c: Math.floor(x / TILE), r: Math.floor(y / TILE) });

const F = (n: number) => 'F'.repeat(n);
const g = (n: number) => 'g'.repeat(n);
const w = (n: number) => 'w'.repeat(n);
const p = (n: number) => 'p'.repeat(n);

/** The old placeholder map, kept for the placeholder art and its tests. */
export const PLACEHOLDER_MAP = parseMap([
  F(24),
  F(11) + p(1) + F(12),
  F(2) + g(9) + p(1) + g(9) + F(3),
  F(1) + g(10) + p(1) + g(11) + F(1),
  F(1) + g(10) + p(1) + g(6) + w(3) + g(2) + F(1),
  F(1) + g(9) + p(2) + g(5) + w(4) + g(2) + F(1),
  F(1) + g(9) + p(1) + g(6) + w(4) + g(2) + F(1),
  F(1) + g(8) + p(2) + g(7) + w(2) + g(3) + F(1),
  F(1) + g(8) + p(1) + g(13) + F(1),
  F(1) + g(2) + 'bbb' + g(3) + p(1) + g(13) + F(1),
  F(1) + g(2) + 'bbb' + g(3) + p(2) + g(12) + F(1),
  F(1) + g(9) + p(1) + g(12) + F(1),
  F(1) + g(9) + p(1) + g(12) + F(1),
  F(1) + g(9) + p(1) + g(12) + F(1),
  F(2) + g(8) + p(1) + g(11) + F(2),
  F(10) + p(1) + F(13),
]);

const maps = new Map<Biome, BiomeMap>();
const labMaps = new Map<Biome, BiomeMap>();

/**
 * The map for a biome: the Lab's (baked into `lab-maps.ts` for the server; the app
 * registers the same from the Lab's own ground with `setBiomeMap`).
 */
export function mapFor(biome: Biome): BiomeMap {
  const set = maps.get(biome);
  if (set) return set;
  let m = labMaps.get(biome);
  if (!m) {
    const lab = LAB_MAPS[biome];
    m = parseMap(lab.rows, lab.blocked);
    labMaps.set(biome, m);
  }
  return m;
}
export const setBiomeMap = (biome: Biome, map: BiomeMap) => void maps.set(biome, map);

/** Tiles in a biome where something already grows. */
export function takenTiles(s: GameState, biome: Biome): Set<string> {
  const taken = new Set<string>();
  for (const pl of s.plants) {
    if (pl.goneAt || pl.biome !== biome || pl.x === null || pl.y === null || pl.plot !== null) continue;
    const { c, r } = tileAt(pl.x, pl.y);
    taken.add(tileKey(c, r));
  }
  return taken;
}

/** What is being planted, for spacing: a tree needs room for its mega size; anything else only its own tile. */
export interface Planting {
  kind: Plant['kind'];
  pace: Difficulty;
  /** The plant itself, so it never crowds itself. */
  id?: string;
}

/**
 * Tree spacing, from each tree's mega size. Side by side, two trunks stay at
 * least half their crowns' width apart, so grown crowns overlap by half at
 * most. Up and down they may stand a little closer, since the tree in front is
 * drawn over the one behind. Saplings and seeds already claim their mega room.
 */
export const CROWN_SHARE = 0.5;
export const DEPTH_SHARE = 0.75;

/** How wide a tree of this pace grows in this biome at its mega size, in pixels. */
export const megaWidth = (biome: Biome, pace: Difficulty) => MEGA_WIDTH[biome]?.[pace] ?? 85;

/** How far apart two trees are against the room they need: under 1 is too close. */
function roomBetween(biome: Biome, a: { c: number; r: number; pace: Difficulty }, b: { c: number; r: number; pace: Difficulty }) {
  const rx = ((megaWidth(biome, a.pace) + megaWidth(biome, b.pace)) / 2) * CROWN_SHARE;
  const ry = rx * DEPTH_SHARE;
  return Math.hypot(((a.c - b.c) * TILE) / rx, ((a.r - b.r) * TILE) / ry);
}

/** The trees that a new tree on this tile would grow too close to. Anything that isn't a tree crowds nothing. */
export function crowdedBy(s: GameState, biome: Biome, c: number, r: number, what: Planting): Plant[] {
  if (what.kind !== 'tree') return [];
  return s.plants.filter((p) => {
    if (p.kind !== 'tree' || p.goneAt || p.biome !== biome || p.x === null || p.y === null || p.plot !== null || p.id === what.id) return false;
    const t = tileAt(p.x, p.y);
    return roomBetween(biome, { c, r, pace: what.pace }, { ...t, pace: p.pace }) < 1;
  });
}

/** Why a tile can't take a planting: not open ground, something already there, or too close to a tree. */
export type PlantingProblem = 'ground' | 'taken' | 'crowded';

export function plantingProblem(
  s: GameState, biome: Biome, c: number, r: number, opts: { props?: boolean; what?: Planting } = {},
): PlantingProblem | null {
  const map = mapFor(biome);
  // A player who moved or put away a piece of scenery has changed where the props are, and only the
  // app knows the new picture; the server then checks the ground itself but not the props on it.
  const props = opts.props ?? !(s.profile.scenes?.[biome] && (Object.keys(s.profile.scenes[biome]!.moves).length || s.profile.scenes[biome]!.hidden.length));
  const open = props ? isPlantable(map, c, r) : PLANTABLE.has(letterAt(map, c, r));
  if (!open) return 'ground';
  if (takenTiles(s, biome).has(tileKey(c, r))) return 'taken';
  if (opts.what && crowdedBy(s, biome, c, r, opts.what).length) return 'crowded';
  return null;
}

export function canPlantAt(s: GameState, biome: Biome, x: number, y: number, opts: { props?: boolean; what?: Planting } = {}): boolean {
  const { c, r } = tileAt(x, y);
  return plantingProblem(s, biome, c, r, opts) === null;
}

/* ---------- suggested spots: where a tree would grow well, glowing while the player chooses ---------- */

const spotOverrides = new Map<Biome, [number, number][]>();
const autoSpots = new WeakMap<BiomeMap, [number, number][]>();

/** Dev mode's spot editor tries out a biome's spots before they're baked; null goes back to the baked ones. */
export function setSuggestedSpots(biome: Biome, spots: [number, number][] | null) {
  if (spots) spotOverrides.set(biome, spots);
  else spotOverrides.delete(biome);
}

/** Whether a biome's spots were placed by hand (baked or being edited) rather than worked out. */
export const handPlacedSpots = (biome: Biome) => spotOverrides.has(biome) || !!SUGGESTED_SPOTS[biome];

/**
 * A biome's suggested tree spots: Newt's, when he has placed them, otherwise an
 * even spread over its open ground, starting near the middle and adding the
 * tile farthest from the rest until no tile has room for an average tree.
 */
export function suggestedSpots(biome: Biome): [number, number][] {
  const set = spotOverrides.get(biome) ?? SUGGESTED_SPOTS[biome];
  if (set) return set;
  const map = mapFor(biome);
  let spots = autoSpots.get(map);
  if (!spots) {
    spots = spread(biome, map);
    autoSpots.set(map, spots);
  }
  return spots;
}

function spread(biome: Biome, map: BiomeMap): [number, number][] {
  const open: [number, number][] = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) if (isPlantable(map, c, r)) open.push([c, r]);
  if (!open.length) return [];
  const widths = Object.values(MEGA_WIDTH[biome] ?? {});
  const avg = widths.length ? widths.reduce((a, b) => a + b, 0) / widths.length : 85;
  const rx = avg * CROWN_SHARE;
  const ry = rx * DEPTH_SHARE;
  const room = (a: [number, number], b: [number, number]) => Math.hypot(((a[0] - b[0]) * TILE) / rx, ((a[1] - b[1]) * TILE) / ry);
  const mc = open.reduce((t, [c]) => t + c, 0) / open.length;
  const mr = open.reduce((t, [, r]) => t + r, 0) / open.length;
  let first = open[0]!;
  for (const t of open) if (Math.hypot(t[0] - mc, t[1] - mr) < Math.hypot(first[0] - mc, first[1] - mr)) first = t;
  const chosen: [number, number][] = [first];
  const nearest = open.map((t) => room(t, first));
  for (;;) {
    let best = -1;
    for (let i = 0; i < open.length; i++) if (best < 0 || nearest[i]! > nearest[best]!) best = i;
    if (best < 0 || nearest[best]! < 1) break;
    const t = open[best]!;
    chosen.push(t);
    for (let i = 0; i < open.length; i++) nearest[i] = Math.min(nearest[i]!, room(open[i]!, t));
  }
  return chosen;
}

/** The suggested spots that could take this planting right now. */
export function openSuggestedSpots(s: GameState, biome: Biome, what: Planting): [number, number][] {
  return suggestedSpots(biome).filter(([c, r]) => plantingProblem(s, biome, c, r, { what, props: true }) === null);
}

export type Area = 'anywhere' | 'center' | 'north' | 'south' | 'east' | 'west' | 'water';

/**
 * A free, plantable tile, preferring an area ("by the water", "in the north").
 * Deterministic for a given garden and `salt`, so a retried call picks the same spot.
 * Given what is being planted, a tree keeps its room from other trees and
 * prefers a suggested spot.
 */
export function findOpenSpot(s: GameState, biome: Biome, area: Area = 'anywhere', salt = '', what?: Planting): { x: number; y: number } | null {
  const map = mapFor(biome);
  const taken = takenTiles(s, biome);
  // A tree goes to a suggested spot when one is free in the area asked for.
  const suggested = new Set(what?.kind === 'tree' ? suggestedSpots(biome).map(([c, r]) => tileKey(c, r)) : []);
  const water: [number, number][] = [];
  map.rows.forEach((row, r) => [...row].forEach((ch, c) => (ch === 'w' || ch === 'r') && water.push([c, r])));
  let best: { c: number; r: number; score: number } | null = null;
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (!isPlantable(map, c, r) || taken.has(tileKey(c, r))) continue;
      if (what && crowdedBy(s, biome, c, r, what).length) continue;
      // Breathing room: prefer tiles with no planted neighbours, so saplings don't crowd.
      let crowd = 0;
      for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) if ((dr || dc) && taken.has(tileKey(c + dc, r + dr))) crowd++;
      const dist = {
        anywhere: 0,
        center: Math.hypot(c - (COLS - 1) / 2, r - (ROWS - 1) / 2),
        north: r,
        south: ROWS - r,
        east: COLS - c,
        west: c,
        water: water.length ? Math.min(...water.map(([wc, wr]) => Math.hypot(wc - c, wr - r))) : 0,
      }[area];
      let h = 2166136261;
      for (const ch of `${salt}|${c}|${r}`) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
      const score = dist * 10 + crowd * 6 + ((h >>> 0) % 1000) / 1000 * (area === 'anywhere' ? 20 : 3) - (suggested.has(tileKey(c, r)) ? 15 : 0);
      if (!best || score < best.score) best = { c, r, score };
    }
  }
  return best ? anchorOf(best.c, best.r) : null;
}
